import { Dialog, DialogContent } from '@mui/material';
import { ProductGroups } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useCoPilotOnboardingStore from 'stores/co-pilot-onboarding-store';
import { makeStyles } from 'tss-react/mui';
import CoPilotExplanation from './co-pilot-explanation';
import CoPilotIntroduction from './co-pilot-introduction';

interface Props {
  productGroup: ProductGroups;

  handleOpenCreateCampaignDialog(): void;
}

const CoPilotOnboardingJourney: React.FC<Props> = ({ productGroup, handleOpenCreateCampaignDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { open, onboardingStep, handleCloseCoPilotOnboardingDialog, setOnboardingStep } = useCoPilotOnboardingStore((state) => ({
    open: state.open,
    onboardingStep: state.onboardingStep,
    handleCloseCoPilotOnboardingDialog: state.handleCloseCoPilotOnboardingDialog,
    setOnboardingStep: state.setOnboardingStep,
  }));

  const MAX_ONBOARDING_STEP = 5;

  const handleClose = () => {
    handleCloseCoPilotOnboardingDialog();
    handleOpenCreateCampaignDialog();
  };

  const handleIncreaseOnboardingStep = () => {
    setOnboardingStep(Math.min(onboardingStep + 1, MAX_ONBOARDING_STEP));
  };

  const handleDecreaseOnboardingStep = () => {
    setOnboardingStep(Math.max(onboardingStep - 1, 0));
  };

  const renderOnboardingStep = () => {
    switch (onboardingStep) {
      case 0:
        return <CoPilotIntroduction productGroup={productGroup} />;
      case 1:
        return <CoPilotExplanation stage={1} productGroup={productGroup} />;
      case 2:
        return <CoPilotExplanation stage={2} productGroup={productGroup} />;
      case 3:
        return <CoPilotExplanation stage={3} productGroup={productGroup} />;
      case 4:
        return <CoPilotExplanation stage={4} productGroup={productGroup} />;
      case 5:
        return <CoPilotExplanation stage={5} productGroup={productGroup} handleClose={handleClose} />;
      default:
        return null;
    }
  };
  return (
    <Dialog classes={{ paper: classes.paper }} open={open} maxWidth={'xl'} fullWidth={true}>
      <DialogContent className={classes.dialogContent}>{renderOnboardingStep()}</DialogContent>
      <div className={classes.actions}>
          {onboardingStep > 0 ? <PaiLink text={t('back')} size={'lg'} variant={'default'} action={handleDecreaseOnboardingStep} /> : null}
          {onboardingStep < MAX_ONBOARDING_STEP ? <PaiButton text={t('continue')} border={'rounded'} action={handleIncreaseOnboardingStep} /> : null}
        </div>
    </Dialog>
  );
};

const useStyles = makeStyles()(() => ({
  paper: {
    position: 'relative',
    borderRadius: '16px',
    height: '100%',
    maxHeight: 'calc(960px - 60px - 64px)',
    maxWidth: 'calc(1920px - 256px)',
  },
  dialogContent: {
    padding: '0px !important',
  },
  actions: {
    position: 'absolute',
    bottom: '16px',
    right: '0px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '0px 16px',
    gap: '16px',
  },
}));

export default CoPilotOnboardingJourney;
